import * as React from 'react';
import { ModalBase } from '../components/ModalBase';
import { useSubscription } from '../hooks/useSubscription';
import { Box, Button, Divider, Skeleton, Slider, TextField, Typography } from '@mui/material';
import { FlexBoxColumn, FlexBoxInline, InputBaseSx, PrimaryBtnSx, SliderSx } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';
import { useSelector } from 'react-redux';
import { getAppConfig, getIsAuthorized } from '../reducers/main/mainReducer';
import { LicenseTypeEnum } from '../@shared/Models/Models';
import { LicenseConfiguration, LicenseExpirationTypeEnum, LicensePrices, LicenseValue } from '../@shared/Models/License';
import { useDispatch } from 'react-redux';
import { NotificationModelType, NotificationModel } from '../reducers/main/mainModel';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';

const columnWidth = 250;

export const withSubscription = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const {
    isDialogOpen,
    setIsDialogOpen,
    subscriptions,
    isLoadingSubscriptions,
    onLoadPrice,
    onUpdateLicense,
    isProcessing,
    onApplyTrial
  } = useSubscription();
  const appConfig = useSelector(getAppConfig)
  const notification = React.useMemo(() => {
    if (!appConfig.isLicenseDisabled) {
      return undefined;
    }

    return {
      type: NotificationModelType.Warning,
      message: `License update is disabled. Please contact "Contact@mlartisan.com" to update license.`,
      date: new Date()
    }
  }, [appConfig.isLicenseDisabled]);

  const subscriptionList = React.useMemo(() => {
    if (appConfig.isSelfHostedServiceEnabled) {
      return subscriptions.filter(x => x.isSelfHosted)
    }

    return subscriptions.filter(x => !x.isSelfHosted);
  }, [appConfig.isSelfHostedServiceEnabled, subscriptions]);

  return <>
    <ModalBase
      isOpen={isDialogOpen}
      onCancel={() => setIsDialogOpen(false)}
      title="Upgrade subscription"
      width={subscriptionList.length > 2 ? subscriptionList.length * columnWidth : 750}
      isProcessing={isProcessing}
      notification={notification}
      isDisabledSave={isProcessing}
    >
      <Box sx={{ ...FlexBoxInline, alignItems: "stretch", padding: "20px 0" }}>
        {!isLoadingSubscriptions && subscriptionList && subscriptionList.length > 0 && subscriptionList.map((x, index) => {
          return <SubscriptionDetails
            license={x}
            key={x.typeId}
            onLoadPrice={onLoadPrice}
            onUpdateLicense={onUpdateLicense}
            onApplyTrial={onApplyTrial}
            isShowDivider={index !== subscriptionList.length - 1}
            isProcessing={isProcessing} />
        })}
        {isLoadingSubscriptions && <>
            <SubscriptionDetailsSkeleton />
            <Divider orientation="vertical" flexItem />
            <SubscriptionDetailsSkeleton />
            <Divider orientation="vertical" flexItem />
            <SubscriptionDetailsSkeleton />
          </>
        }
      </Box>
    </ModalBase>
    <Component {...props as T} />
  </>
}

type SubscriptionBtnProps = {
  currentTypeId?: string,
  typeId: LicenseTypeEnum,
  onUpdateLicense: () => Promise<void>,
  initialPrice?: number,
  price?: number
}

export const SubscriptionBtn = (props: SubscriptionBtnProps) => {
  const licenseTypeOrder: Record<LicenseTypeEnum, number> = {
    [LicenseTypeEnum.Trial]: 0,
    [LicenseTypeEnum.Basic]: 1,
    [LicenseTypeEnum.Pro]: 2,
    [LicenseTypeEnum.Enterprise]: 3,
    [LicenseTypeEnum.SelfHostedTrial]: 4,
    [LicenseTypeEnum.SelfHosted]: 5
  };

  const isDowngrade = (props.currentTypeId && licenseTypeOrder[props.currentTypeId as LicenseTypeEnum] > licenseTypeOrder[props.typeId as LicenseTypeEnum]) ||
    (props.initialPrice && props.price && props.initialPrice > props.price);

  const isDisabled = props.currentTypeId === props.typeId && !!props.initialPrice && !!props.price && props.initialPrice === props.price;

  return <Button onClick={props.onUpdateLicense} sx={PrimaryBtnSx} disabled={isDisabled}>
    {!isDowngrade ? "Upgrade" : "Downgrade"}
  </Button>
}

export const SubscriptionDetails = ({ license, onLoadPrice, onUpdateLicense, isShowDivider, onApplyTrial, isProcessing }: {
  license: LicenseValue,
  onLoadPrice: (conf: LicenseConfiguration) => Promise<LicensePrices>,
  onUpdateLicense: (conf: LicenseConfiguration) => Promise<void>,
  onApplyTrial: () => Promise<void>,
  isShowDivider: boolean,
  isProcessing: boolean
}) => {
  const user = useSelector(getIsAuthorized)
  const isTrial = license.typeId === LicenseTypeEnum.Trial || license.typeId === LicenseTypeEnum.SelfHostedTrial;
  const [isLoadingPrice, setIsLoadingPrice] = React.useState<boolean>(false);
  const [price, setPrice] = React.useState<number>(0);
  const [cpuTimeInMinutes, setCpuTimeInMinutes] = React.useState<number>(license.defaultCpuTimeInMinutes ?? license.minCpuTimeInMin);
  const [countOfSchemas, setCountOfSchemas] = React.useState<number>(license.defaultCountOfSchemas ?? license.minCountOfSchemas);
  const [countOfModels, setCountOfModels] = React.useState<number>(license.defaultCountOfModels ?? license.minCountOfModels);
  const [datasetSizeForSchemaMb, setDatasetSizeForSchemaMb] = React.useState<number>(license.defaultDatasetSizeForSchemaMb ?? license.minDatasetSizeForSchemaMb);
  const [seats, setSeats] = React.useState<number>(user?.license?.numberOfSeats || 1);
  //const [instances, setInstances] = React.useState<number>(1);

  const [initialPrice, setInitialPrice] = React.useState<number | undefined>(undefined);
  const appConfig = useSelector(getAppConfig)

  const licenseConfig = React.useMemo(() => {
    return {
      typeId: license.typeId,
      expirationType: LicenseExpirationTypeEnum.Month,
      cpuTimeInMinutes: cpuTimeInMinutes,
      isCalibratingEnabled: license.isCalibratingEnabled,
      countOfSchemas: countOfSchemas,
      countOfModels: countOfModels,
      numberOfFeatures: license.maxNumberOfFeatures,
      datasetSizeForSchemaMb: datasetSizeForSchemaMb,
      numberOfSeats: seats,
      //numberOfInstances: instances
    } as LicenseConfiguration;
  }, [license.typeId, license.isCalibratingEnabled, license.maxNumberOfFeatures, cpuTimeInMinutes, countOfSchemas, countOfModels, datasetSizeForSchemaMb, seats])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if(appConfig.isLicenseDisabled){
        return;
      }
      setIsLoadingPrice(true);
      onLoadPrice(licenseConfig).then(x => {
        setPrice(x.usd);
        if (!initialPrice) {
          setInitialPrice(x.usd);
        }
        setIsLoadingPrice(false);
      })
    }, 1000);

    return () => clearTimeout(timer);
  }, [appConfig.isLicenseDisabled, initialPrice, licenseConfig, onLoadPrice])


  return <>
    <Box sx={{ ...FlexBoxColumn, justifyContent: "space-between", width: "100%", alignItems: "center", padding: "0 10px" }}>
      <Box sx={{ ...FlexBoxColumn, alignItems: "center", width: "100%" }}>
        <Typography sx={{ fontSize: Sizes.pageThirdTitle, color: Palette.textColorPrimary, marginBottom: "0px" }}>{license.name}</Typography>
        <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "10px", display: "block", height: "30px", textAlign: "center" }}>{license.description}</Typography>
        <Divider flexItem />
        <SubscriptionDetailsItem text='requests / min' value={license.maxRequestsPerTimeWindow} isTrial={isTrial} isConfigurable={false} minValue={license.maxRequestsPerTimeWindow} />
        <Divider flexItem />
        <SubscriptionDetailsItem text='CPU min / day' value={cpuTimeInMinutes} isTrial={isTrial} isConfigurable={license.isConfigurable} minValue={license.minCpuTimeInMin} maxValue={license.maxCpuTimeInMin} onChangeValue={setCpuTimeInMinutes} step={50} isShowSlider />
        <Divider flexItem />
        <SubscriptionDetailsItem text='schemas' value={countOfSchemas} isTrial={isTrial} isConfigurable={license.isConfigurable} minValue={license.minCountOfSchemas} maxValue={license.maxCountOfSchemas} onChangeValue={setCountOfSchemas} step={1} isShowSlider />
        <Divider flexItem />
        <SubscriptionDetailsItem text='models' value={countOfModels} isTrial={isTrial} isConfigurable={license.isConfigurable} minValue={license.minCountOfModels} maxValue={license.maxCountOfModels} onChangeValue={setCountOfModels} step={5} isShowSlider />
        <Divider flexItem />
        <SubscriptionDetailsItem text='MB / schema' value={datasetSizeForSchemaMb} isTrial={isTrial} isConfigurable={license.isConfigurable} minValue={license.minDatasetSizeForSchemaMb} maxValue={license.maxDatasetSizeForSchemaMb} onChangeValue={setDatasetSizeForSchemaMb} step={100} isShowSlider />
        <Divider flexItem />
        <SubscriptionDetailsItem text='model in queue' value={license.maxCountModelsInQueue} isTrial={isTrial} isConfigurable={false} minValue={license.maxCountModelsInQueue} />
        <Divider flexItem />
        <SubscriptionDetailsItem text={!license.isCalibratingEnabled ? "-" : "Calibration"} isTrial={isTrial} isConfigurable={false} />
        {license.isSupportsSeats && <Divider flexItem />}
        {license.isSupportsSeats && <SubscriptionDetailsItem text='seats' value={seats} isTrial={isTrial} isConfigurable={license.isConfigurable} minValue={1} maxValue={999999} onChangeValue={setSeats} />}
        {/* {appConfig.isSelfHostedServiceEnabled && <Divider flexItem />}
        {appConfig.isSelfHostedServiceEnabled && <SubscriptionDetailsItem text='instances' value={instances} isTrial={isTrial} isConfigurable={license.isConfigurable} minValue={1} maxValue={999999} onChangeValue={setInstances} />} */}

      </Box>
      <Box sx={{ ...FlexBoxColumn, alignItems: "center", width: "100%" }}>
        <Box sx={{ marginBottom: "40px", marginTop: "40px" }}>
          {isLoadingPrice && !appConfig.isLicenseDisabled && <Skeleton variant="rectangular" width={50} height={20} />}
          {!isLoadingPrice && !appConfig.isLicenseDisabled && <Typography sx={{ fontSize: Sizes.pageThirdTitle, color: isTrial ? Palette.textColorSecondary : Palette.textColorPrimary }}>{price ? `${price}$` : "Free"}</Typography>}
        </Box>
        {isTrial && <Button onClick={onApplyTrial} sx={PrimaryBtnSx} disabled={!!user?.licenseKey || isProcessing}>
          Start For Free
        </Button>}
        {!isTrial && !appConfig.isLicenseDisabled && <SubscriptionBtn price={price} initialPrice={initialPrice} onUpdateLicense={() => onUpdateLicense(licenseConfig)} typeId={license.typeId} currentTypeId={user?.license?.licenseType} />}
      </Box>
    </Box>
    {isShowDivider && <Divider orientation="vertical" flexItem />}
  </>
}

export const SubscriptionDetailsSkeleton = () => {
  return <>
    <Box sx={{ ...FlexBoxColumn, justifyContent: "space-between", width: "100%", alignItems: "center", padding: "0 10px" }}>
      <Box sx={{ ...FlexBoxColumn, alignItems: "center", width: "100%" }}>
        <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:100 }} />
        <Skeleton variant="text" sx={{ fontSize: Sizes.textSecondary, width:200 }} />
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
        <Divider flexItem />
        <Box sx={{padding: "10px"}}>
          <Skeleton variant="text" sx={{ fontSize: Sizes.textPrimary, width:150 }} />
        </Box>
      </Box>
    </Box>
  </>
}

const SubscriptionDetailsItem = (props: {
  isConfigurable: boolean,
  isTrial: boolean,
  value?: number,
  onChangeValue?: (value: number) => void,
  minValue?: number,
  maxValue?: number,
  text: string,
  isShowSlider?: boolean,
  step?: number
}) => {
  const dispatch = useDispatch();
  const [tempValue, setTempValue] = React.useState<number | undefined>(props.value);
  const integratedTextFieldSx = {
    ...InputBaseSx,
    margin: 0,
    '& .MuiInputBase-input': {
      padding: "2px",
      width: props.maxValue ? props.maxValue.toString().length * 10 + "px" : "50px",
      fontSize: Sizes.textPrimary,
      color: Palette.textColorPrimary
    }
  }

  if (props.value === 0) {
    return <Box sx={{ ...FlexBoxColumn, padding: "5px", width: "100%", height: "50px", alignItems: "center", justifyContent: "space-around" }}>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: props.isTrial ? Palette.textColorSecondary : Palette.textColorPrimary }}>{`Unlimited ${props.text}`}</Typography>
    </Box>
  }

  if (!props.isConfigurable) {
    return <Box sx={{ ...FlexBoxColumn, padding: "5px", width: "100%", height: "50px", alignItems: "center", justifyContent: "space-around" }}>
      <Typography sx={{ fontSize: Sizes.textPrimary, color: props.isTrial ? Palette.textColorSecondary : Palette.textColorPrimary }}>{props.value ? `${props.value} ${props.text}` : props.text}</Typography>
    </Box>
  }

  return <Box sx={{ ...FlexBoxColumn, padding: "5px", width: "100%", height: "50px", alignItems: "center", justifyContent: "space-around" }}>
    <Box sx={{ ...FlexBoxInline }}>
      <TextField sx={integratedTextFieldSx} value={tempValue} onChange={(v) => {
        const value = Number(v.currentTarget.value);
        if (Number.isNaN(value)) {
          return;
        }
        setTempValue!(value)
      }}
        onBlur={() => {
          if (tempValue! < props.minValue!) {
            setTempValue(props.minValue!)
            props.onChangeValue!(props.minValue!);
            dispatch(onThrowAlert({
              message: `Min value for ${props.text} is ${props.minValue}`,
              lifeTime: 3000,
              type: NotificationModelType.Warning
            } as NotificationModel));
            return;
          }
          if (tempValue! > props.maxValue!) {
            setTempValue!(props.maxValue!)
            props.onChangeValue!(props.maxValue!);
            dispatch(onThrowAlert({
              message: `Max value for ${props.text} is ${props.maxValue}`,
              lifeTime: 3000,
              type: NotificationModelType.Warning
            } as NotificationModel));
            return;
          }

          if (tempValue) {
            props.onChangeValue!(tempValue);
          }
        }} />
      <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>{props.text}</Typography>
    </Box>
    {props.isShowSlider && <Slider sx={SliderSx} onChange={(_, v) => {
      props.onChangeValue!(Number(v))
      setTempValue!(Number(v))
    }} value={tempValue} step={props.step!} marks min={props.minValue} max={props.maxValue} />}
  </Box>
}